import { api } from '../client';
import type { PageParams } from '@/types/api';
import type { UUID } from '@/types/domain';
import type {
  Asset,
  AssetAudit,
  AssetAuditLine,
  AssetCategory,
  AssetCategoryInput,
  AssetConditionInput,
  AssetDashboard,
  AssetEvent,
  AssetInput,
  AssetLocation,
  AssetLocationInput,
  AssetRecordStatus,
  AssetSearchParams,
  AssignAssetInput,
  AuditCheckInput,
  CompleteMaintenanceInput,
  FoundAssetInput,
  MarkMissingLostResult,
  MoveAssetInput,
  StartAuditInput,
  StockItem,
  StockItemInput,
  StockLookups,
  StockMovement,
  StockMovementInput,
  StockSearchParams,
} from '@/types/asset';

/*
 * The asset module's endpoints.
 *
 * Every change to where an asset is, who has it, what shape it is in or
 * whether it still exists is a named operation — there is no generic "set
 * status" call, and `update` carries descriptive fields only. Refusals (a
 * disposed asset, an over-issue, a closed count) come back as server errors
 * with the reason, and the screens show that reason verbatim.
 */

// ------------------------------------------------------------------- assets

export const assetsApi = {
  /** Search matches tag, name, serial and model — on the server. */
  list: (params: PageParams & AssetSearchParams) =>
    api.getPage<Asset>('/assets', { params }),

  /** Counts by status, expiring warranties, low stock, open counts. */
  dashboard: () => api.get<AssetDashboard>('/assets/dashboard'),

  get: (id: UUID) => api.get<Asset>(`/assets/${id}`),

  /** Everything that ever happened to it, newest first. */
  history: (id: UUID) => api.get<AssetEvent[]>(`/assets/${id}/history`),

  /** The tag is issued by the school's counter. */
  register: (payload: AssetInput) => api.post<Asset>('/assets', payload),

  /** Descriptive details only — location and condition are refused here. */
  update: (id: UUID, payload: AssetInput) => api.put<Asset>(`/assets/${id}`, payload),

  /** To one person or one department. */
  assign: (id: UUID, payload: AssignAssetInput) =>
    api.post<Asset>(`/assets/${id}/assign`, payload),

  /** Return to stock, optionally to a location. */
  unassign: (id: UUID, locationId?: UUID, notes?: string) =>
    api.post<Asset>(`/assets/${id}/unassign`, undefined, { params: { locationId, notes } }),

  move: (id: UUID, payload: MoveAssetInput) => api.post<Asset>(`/assets/${id}/move`, payload),

  changeCondition: (id: UUID, payload: AssetConditionInput) =>
    api.post<Asset>(`/assets/${id}/condition`, payload),

  startMaintenance: (id: UUID, notes?: string) =>
    api.post<Asset>(`/assets/${id}/maintenance/start`, undefined, { params: { notes } }),

  /** Needs the condition it came back in; returns it to its custodian if it had one. */
  completeMaintenance: (id: UUID, payload: CompleteMaintenanceInput) =>
    api.post<Asset>(`/assets/${id}/maintenance/complete`, payload),

  /** A reason is required. */
  reportLost: (id: UUID, reason: string) =>
    api.post<Asset>(`/assets/${id}/lost`, undefined, { params: { reason } }),

  markFound: (id: UUID, payload: FoundAssetInput) =>
    api.post<Asset>(`/assets/${id}/found`, payload),

  /** Final. A reason is required; the record and history remain. */
  dispose: (id: UUID, reason: string) =>
    api.post<Asset>(`/assets/${id}/dispose`, undefined, { params: { reason } }),
};

// ----------------------------------------------------------- reference data

export const assetCategoriesApi = {
  /** Every category, with how many assets and stock items use each. Not paged. */
  list: () => api.get<AssetCategory[]>('/asset-categories'),
  create: (payload: AssetCategoryInput) =>
    api.post<AssetCategory>('/asset-categories', payload),
  /** Consumable-or-not is fixed once the category is used. */
  update: (id: UUID, payload: AssetCategoryInput) =>
    api.put<AssetCategory>(`/asset-categories/${id}`, payload),
  /** Retire or restore. */
  setStatus: (id: UUID, status: AssetRecordStatus) =>
    api.patch<AssetCategory>(`/asset-categories/${id}/status`, undefined, {
      params: { status },
    }),
};

export const assetLocationsApi = {
  /** Every location (optionally one campus's), with its asset count. Not paged. */
  list: (campusId?: UUID) =>
    api.get<AssetLocation[]>('/asset-locations', { params: { campusId } }),
  create: (payload: AssetLocationInput) =>
    api.post<AssetLocation>('/asset-locations', payload),
  update: (id: UUID, payload: AssetLocationInput) =>
    api.put<AssetLocation>(`/asset-locations/${id}`, payload),
  /** Retiring is refused while assets are recorded there. */
  setStatus: (id: UUID, status: AssetRecordStatus) =>
    api.patch<AssetLocation>(`/asset-locations/${id}/status`, undefined, {
      params: { status },
    }),
};

// ------------------------------------------------------------------- counts

export const assetAuditsApi = {
  /** Newest first, with their tallies. */
  list: (params: { page?: number; size?: number }) =>
    api.getPage<AssetAudit>('/asset-audits', { params }),

  get: (id: UUID) => api.get<AssetAudit>(`/asset-audits/${id}`),

  /** Every asset on the count. Unchecked lines stay unchecked — never presumed. */
  lines: (id: UUID) => api.get<AssetAuditLine[]>(`/asset-audits/${id}/lines`),

  /** Snapshots every non-disposed asset in scope. */
  start: (payload: StartAuditInput) => api.post<AssetAudit>('/asset-audits', payload),

  /** Record one asset as found or not found. */
  check: (id: UUID, payload: AuditCheckInput) =>
    api.post<AssetAudit>(`/asset-audits/${id}/checks`, payload),

  /** Final. Discrepancies are reported, not applied. */
  close: (id: UUID, notes?: string) =>
    api.post<AssetAudit>(`/asset-audits/${id}/close`, undefined, { params: { notes } }),

  /** A separate, explicit step on a closed count. Needs ASSET_MANAGE. */
  markMissingAsLost: (id: UUID) =>
    api.post<MarkMissingLostResult>(`/asset-audits/${id}/mark-missing-lost`),
};

// -------------------------------------------------------------------- stock

export const stockApi = {
  /** lowOnly lists items at or below their reorder level. */
  list: (params: PageParams & StockSearchParams) =>
    api.getPage<StockItem>('/stock-items', { params }),

  /** Active consumable categories and active locations, under STOCK_VIEW alone. */
  lookups: () => api.get<StockLookups>('/stock-items/lookups'),

  get: (id: UUID) => api.get<StockItem>(`/stock-items/${id}`),

  /** Starts at zero; receive stock to put some on the shelf. */
  create: (payload: StockItemInput) => api.post<StockItem>('/stock-items', payload),

  /** Details only — never the quantity. */
  update: (id: UUID, payload: StockItemInput) =>
    api.put<StockItem>(`/stock-items/${id}`, payload),

  setStatus: (id: UUID, status: AssetRecordStatus) =>
    api.patch<StockItem>(`/stock-items/${id}/status`, undefined, { params: { status } }),

  /** Receive, issue or adjust. Over-issue and a reasonless ADJUST are refused. */
  move: (id: UUID, payload: StockMovementInput) =>
    api.post<StockItem>(`/stock-items/${id}/movements`, payload),

  /** The item's ledger, newest first (server default size 25). */
  movements: (id: UUID, params: { page?: number; size?: number }) =>
    api.getPage<StockMovement>(`/stock-items/${id}/movements`, { params }),
};
